// Keyboard shortcuts: "Alt+M", "Ctrl+K", "Escape", "F8", "Ctrl+F8", "Shift+?" …
export interface KeyLike { key: string; altKey: boolean; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }

export interface Shortcut { alt: boolean; ctrl: boolean; shift: boolean; key: string }

export function parseShortcut(spec: string): Shortcut {
  const parts = spec.split("+").map((p) => p.trim()).filter(Boolean);
  const key = (parts.pop() ?? "").toLowerCase();
  const mods = new Set(parts.map((p) => p.toLowerCase()));
  return { alt: mods.has("alt"), ctrl: mods.has("ctrl") || mods.has("cmd"), shift: mods.has("shift"), key: key === "esc" ? "escape" : key };
}

// Ctrl and the Mac's Cmd count as the same; letters ignore case; with Alt, the physical letter counts (Alt+M on a Mac types "µ")
export function matches(e: KeyLike, spec: string | Shortcut, code?: string): boolean {
  const s = typeof spec === "string" ? parseShortcut(spec) : spec;
  if (s.alt !== e.altKey || s.ctrl !== (e.ctrlKey || e.metaKey)) return false;
  const k = e.key.toLowerCase();
  if (s.key.length === 1 && /[a-z0-9]/.test(s.key) && code) {
    const physical = code.replace(/^Key|^Digit/, "").toLowerCase();
    if (physical === s.key) return s.shift === e.shiftKey || !/[a-z]/.test(s.key);
  }
  if (k !== s.key) return false;
  // a shifted symbol like "?" is typed with Shift; don't insist on it
  return s.key.length === 1 && !/[a-z0-9]/.test(s.key) ? true : s.shift === e.shiftKey;
}

export function label(spec: string): string {
  const s = parseShortcut(spec);
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  const key = s.key === "escape" ? "Esc" : s.key.length === 1 ? s.key.toUpperCase() : s.key.toUpperCase();
  return [s.ctrl ? (mac ? "⌘" : "Ctrl") : "", s.alt ? (mac ? "⌥" : "Alt") : "", s.shift && s.key.length > 1 ? "Shift" : "", key]
    .filter(Boolean).join(mac ? "" : "+");
}

// typing in a field: single keys (and Escape inside a text box) belong to the field, not to navigation
export function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  return el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
}
